import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { ensureActiveSession } from '@/lib/session';
import { useAuth } from '@/app/AuthProvider';
import type {
  Inserts,
  TaskPriority,
  TaskStatus,
  Tables,
  Updates,
} from '@/types/database';
import {
  coerceWorkloadHours,
  endOfLocalWeekSunday,
  localDateKey,
  startOfLocalWeekMonday,
} from '@/features/projects/workload';

/**
 * Hooks for the Tasks section (migration 31, originally "Project
 * Management"). A project here is a shared task list; the section is
 * the cross-department hub, and a project's optional `department` tag
 * (migration 141) lets a department section list just its own
 * projects — see src/features/projects/departments.ts.
 *
 * Visibility model (DB-enforced via RLS, migration 149):
 *   - a project is private to the people on it. Everyone — super_admin
 *     included — sees only the projects whose project_members row maps
 *     to them, OR that they themselves created (the auto-membership
 *     trigger covers that for new projects, but the OR keeps backfilled
 *     rows visible too). Role grants no visibility on its own.
 *
 * Write model:
 *   - Project metadata + member list: creator, or a super_admin who is
 *     on the project.
 *   - Tasks: any project member.
 *
 * The Supabase types stub in src/types/database.ts now includes the
 * three new tables, so we don't need the `'projects' as never` escape
 * hatches that the inventory hooks use for the (otherwise un-typed)
 * Phase 2/3 tables.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type Project = Tables<'projects'>;
export type ProjectFolder = Tables<'project_folders'>;
export type ProjectMember = Tables<'project_members'>;
export type ProjectSection = Tables<'project_sections'>;
export type Task = Tables<'tasks'>;
export type TaskComment = Tables<'task_comments'>;
export type TaskAttachment = Tables<'task_attachments'>;

/** Comment row joined with the author's display fields. */
export type TaskCommentWithAuthor = TaskComment & {
  author: Pick<
    Tables<'profiles'>,
    'id' | 'full_name' | 'email' | 'avatar_url'
  > | null;
};

/** Attachment row joined with the uploader's display fields. */
export type TaskAttachmentWithUploader = TaskAttachment & {
  uploader: Pick<
    Tables<'profiles'>,
    'id' | 'full_name' | 'email' | 'avatar_url'
  > | null;
};

/**
 * The shape we render on the dashboard + sidebar: one project plus its
 * member profiles plus a few aggregate counts. The counts come from a
 * second query (Postgrest doesn't aggregate in a single round-trip)
 * and get merged in `useProjectsWithSummary`.
 */
export interface ProjectWithMembers extends Project {
  project_members: Array<{
    profile_id: string;
    profiles: Pick<
      Tables<'profiles'>,
      'id' | 'full_name' | 'email' | 'avatar_url'
    > | null;
  }>;
}

export interface ProjectSummary extends ProjectWithMembers {
  taskCount: number;
  openTaskCount: number;
  /** Open tasks whose due date has passed (local calendar day). */
  overdueTaskCount: number;
}

/**
 * Display projection of a single assignee on a task. Mirrors the
 * "lite" profile shape used by `ProjectWithMembers.project_members`,
 * so we can flow the same avatar / name / email rendering through
 * both lists.
 */
export type TaskAssigneeProfile = Pick<
  Tables<'profiles'>,
  'id' | 'full_name' | 'email' | 'avatar_url'
>;

/**
 * A task with its full assignee list flattened to an array of profile
 * projections. The DB shape is a row in `task_assignees` per assignee
 * (joined to `profiles`); we collapse that here so the table renderer
 * can iterate `task.assignees` directly without a second lookup.
 *
 * Replaces the old single-assignee `TaskWithAssignee` type — see
 * migration 35 for the schema change. Empty array = unassigned.
 */
export type TaskWithAssignees = Task & {
  assignees: TaskAssigneeProfile[];
  /**
   * Home project of the task, embedded by TASK_SELECT. Optional
   * because optimistic cache writes patch existing rows and never
   * fabricate it. On the project detail page a task whose
   * `project_id` differs from the page's project is a *linked* task
   * (multi-homed via task_project_links, migration 86) and this
   * embed labels the "from <project>" chip.
   */
  project?: Pick<Tables<'projects'>, 'id' | 'name'> | null;
};

/**
 * One row of the task's "Projects" list in the detail drawer: a
 * project the task is linked into (its home project is NOT included
 * here — that's `task.project_id` / `task.project`).
 */
export type TaskProjectLink = {
  project_id: string;
  created_at: string;
  project: Pick<Tables<'projects'>, 'id' | 'name' | 'archived_at'> | null;
};

// ---------------------------------------------------------------------------
// Query-key helpers
// ---------------------------------------------------------------------------

export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (params: { includeArchived?: boolean; department?: string | null }) =>
    [...projectKeys.lists(), params] as const,
  detail: (id: string) => [...projectKeys.all, 'detail', id] as const,
  members: (id: string) => [...projectKeys.all, id, 'members'] as const,
  tasks: (id: string) => [...projectKeys.all, id, 'tasks'] as const,
  sections: (id: string) => [...projectKeys.all, id, 'sections'] as const,
  folders: () => [...projectKeys.all, 'folders'] as const,
};

export const taskKeys = {
  all: ['tasks'] as const,
  comments: (taskId: string) => [...taskKeys.all, taskId, 'comments'] as const,
  projects: (taskId: string) => [...taskKeys.all, taskId, 'projects'] as const,
  attachments: (taskId: string) =>
    [...taskKeys.all, taskId, 'attachments'] as const,
  attachmentUrl: (taskId: string, path: string) =>
    [...taskKeys.all, taskId, 'attachments', path, 'url'] as const,
};

// Storage bucket for files uploaded against a task (see migration 34).
// Path shape is `<organization_id>/<project_id>/<task_id>/<filename>` —
// the storage RLS helper decodes this with split_part().
const TASK_ATTACHMENTS_BUCKET = 'task-attachments';

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

// Column list shared by every projects read so the shape of
// ProjectWithMembers is defined in one place. project_members has TWO
// foreign keys into profiles (profile_id + added_by), so an
// un-qualified `profiles(...)` embed is ambiguous and Postgrest errors
// with "more than one relationship found" — the embed is pinned to
// project_members_profile_id_fkey explicitly.
const PROJECT_COLUMNS =
  'id, organization_id, name, description, created_by, archived_at, position, start_date, end_date, owner_id, folder_id, department, created_at, updated_at';
const PROJECT_WITH_MEMBERS_SELECT = `${PROJECT_COLUMNS}, project_members(profile_id, profiles!project_members_profile_id_fkey(id, full_name, email, avatar_url))`;

export interface ProjectListParams {
  /** Include closed (archived) projects. Default false. */
  includeArchived?: boolean;
  /**
   * Restrict to one department slug (see departments.ts). Omit / null
   * for every project — the Tasks hub view. A department section
   * passes its own slug to list just the projects it owns.
   */
  department?: string | null;
}

/**
 * Lists every project the current user can read. RLS restricts the
 * resultset, so we don't need a client-side filter — but we do hide
 * archived projects unless `includeArchived` is set so the sidebar
 * doesn't accumulate stale entries.
 */
export function useProjects(params?: ProjectListParams) {
  const { profile } = useAuth();
  const includeArchived = params?.includeArchived ?? false;
  const department = params?.department ?? null;
  return useQuery({
    queryKey: projectKeys.list({ includeArchived, department }),
    enabled: !!profile,
    queryFn: async () => {
      let q = supabase
        .from('projects')
        .select(PROJECT_WITH_MEMBERS_SELECT)
        // Primary sort is the user-controlled `position` (migration 36)
        // so drag-and-drop reorders persist across reloads. created_at
        // is the tiebreaker for older rows that share a position from
        // a partial backfill.
        .order('position', { ascending: true })
        .order('created_at', { ascending: false });
      if (!includeArchived) q = q.is('archived_at', null);
      if (department) q = q.eq('department', department);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as ProjectWithMembers[];
    },
  });
}

/**
 * Persist a new ordering for a set of projects. The caller passes the
 * full list of project ids in their desired display order; we generate
 * 1-based positions for the batch and ship them to the
 * `set_project_positions` RPC (migration 36).
 *
 * Optimistically rewrites both the active+archived list cache and the
 * archived-only list cache so the sidebar + dashboard stay in sync the
 * instant the drop completes — the server invalidate then reconciles.
 *
 * Both ProjectWithMembers and ProjectSummary share the same shape for
 * the purposes of reordering (we only touch `position`), so the same
 * mutation services every consumer.
 */
export function useReorderProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderedIds: string[] }) => {
      if (input.orderedIds.length === 0) return;
      // 1-based so a freshly created project (which the trigger places
      // at max(position)+1) lands strictly after the explicit set we're
      // writing here. Avoids a brand-new project getting "swallowed"
      // mid-reorder if it raced the drop.
      const positions = input.orderedIds.map((_, idx) => idx + 1);
      const { error } = await supabase.rpc('set_project_positions', {
        p_ids: input.orderedIds,
        p_positions: positions,
      });
      if (error) throw error;
    },
    onMutate: async (input) => {
      // Cancel both in-flight list queries (with + without archived)
      // so the optimistic write isn't immediately stomped on by a
      // late-arriving fetch.
      await qc.cancelQueries({ queryKey: projectKeys.lists() });

      const order = new Map(input.orderedIds.map((id, idx) => [id, idx + 1]));
      const snapshots: Array<{
        key: ReturnType<typeof projectKeys.list>;
        previous: ProjectWithMembers[] | undefined;
      }> = [];

      // Reorder every cached list query. `projectKeys.lists()` is a
      // PREFIX (['projects', 'list']) and `getQueriesData` also matches
      // the summary cache in `useProjectsWithSummary` (which keys off
      // `[...projectKeys.lists(), 'summary', flag]` and stores a Map,
      // not an array). Skip anything that isn't a project array so we
      // don't try to `.map()` over the summary Map and blow up with
      // "value.map is not a function".
      const lists = qc.getQueriesData<unknown>({
        queryKey: projectKeys.lists(),
      });
      for (const [key, value] of lists) {
        if (!Array.isArray(value)) continue;
        const arr = value as ProjectWithMembers[];
        snapshots.push({
          key: key as ReturnType<typeof projectKeys.list>,
          previous: arr,
        });
        const next = arr
          .map((p) => {
            const pos = order.get(p.id);
            return pos !== undefined ? { ...p, position: pos } : p;
          })
          .sort((a, b) => a.position - b.position);
        qc.setQueryData(key, next);
      }
      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      // Roll back every list cache we touched. Without this a failed
      // RPC would leave the optimistic ordering wedged in until a
      // page reload or the next refetch.
      for (const snap of ctx?.snapshots ?? []) {
        qc.setQueryData(snap.key, snap.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

/**
 * Same as `useProjects` but augmented with task counts. Done as a second
 * round-trip rather than a Postgrest count(*) embed because the latter
 * forces head-only requests and we'd lose the project rows.
 */
export function useProjectsWithSummary(params?: ProjectListParams) {
  const projectsQ = useProjects(params);
  const { profile } = useAuth();
  const summariesQ = useQuery({
    queryKey: [
      ...projectKeys.lists(),
      'summary',
      !!params?.includeArchived,
      params?.department ?? null,
    ],
    enabled: !!profile && !!projectsQ.data,
    queryFn: async () => {
      type Counts = { total: number; open: number; overdue: number };
      const ids = (projectsQ.data ?? []).map((p) => p.id);
      if (ids.length === 0) return new Map<string, Counts>();
      const { data, error } = await supabase
        .from('tasks')
        .select('project_id, is_completed, due_date')
        .in('project_id', ids);
      if (error) throw error;
      const today = localDateKey();
      const counts = new Map<string, Counts>();
      for (const id of ids) counts.set(id, { total: 0, open: 0, overdue: 0 });
      for (const t of data ?? []) {
        const slot = counts.get(t.project_id)!;
        slot.total += 1;
        if (!t.is_completed) {
          slot.open += 1;
          if (t.due_date && t.due_date.slice(0, 10) < today) slot.overdue += 1;
        }
      }
      return counts;
    },
  });

  const data: ProjectSummary[] | undefined = projectsQ.data?.map((p) => {
    const c = summariesQ.data?.get(p.id);
    return {
      ...p,
      taskCount: c?.total ?? 0,
      openTaskCount: c?.open ?? 0,
      overdueTaskCount: c?.overdue ?? 0,
    };
  });

  return {
    data,
    isLoading: projectsQ.isLoading || summariesQ.isLoading,
    isError: projectsQ.isError || summariesQ.isError,
  };
}

export function useProject(id: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: id ? projectKeys.detail(id) : ['projects', 'detail', 'noop'],
    enabled: !!profile && !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select(PROJECT_WITH_MEMBERS_SELECT)
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as unknown as ProjectWithMembers;
    },
  });
}

/**
 * Create a new project + add the supplied profile_ids as members. The
 * creator is auto-added by the DB trigger so we deliberately exclude
 * `auth.uid()` from the membership writes here to avoid PK conflicts —
 * the on-conflict in the trigger would no-op anyway, but it keeps the
 * audit log cleaner.
 */
export function useCreateProject() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      memberProfileIds: string[];
      /** Department slug (departments.ts) or null for none. */
      department?: string | null;
    }) => {
      if (!profile) throw new Error('Not signed in.');
      // Refresh (or fail clearly) before the insert so a silently expired
      // token surfaces as "sign in again" instead of a cryptic RLS 42501.
      await ensureActiveSession();
      const name = input.name.trim();
      if (!name) throw new Error('Project name is required.');

      const insertPayload: Inserts<'projects'> = {
        organization_id: profile.organization_id,
        name,
        created_by: profile.id,
        // The creator starts out as the accountable owner (migration 85
        // backfill convention).
        owner_id: profile.id,
        department: input.department ?? null,
      };
      const { data: project, error: projectErr } = await supabase
        .from('projects')
        .insert(insertPayload)
        .select(PROJECT_COLUMNS)
        .single();
      if (projectErr) throw projectErr;

      const extras = input.memberProfileIds.filter((id) => id !== profile.id);
      if (extras.length > 0) {
        const memberRows: Inserts<'project_members'>[] = extras.map((profileId) => ({
          project_id: project.id,
          profile_id: profileId,
          organization_id: profile.organization_id,
          added_by: profile.id,
        }));
        const { error: memberErr } = await supabase
          .from('project_members')
          .insert(memberRows);
        if (memberErr) {
          // The project itself is good; surface the membership failure
          // so the UI can offer a retry on the share-with list. We
          // explicitly do NOT roll back the project — partial success
          // is recoverable in the detail page's member editor.
          throw new Error(
            `Project created but failed to share with everyone: ${memberErr.message}`,
          );
        }
      }
      return project as Project;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: Updates<'projects'> }) => {
      const { data, error } = await supabase
        .from('projects')
        .update(input.patch)
        .eq('id', input.id)
        .select()
        .single();
      if (error) throw error;
      return data as Project;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

/**
 * Soft-archive a project (sets archived_at = now). Tasks stay put so
 * we don't lose history; the sidebar / dashboard hide the row by
 * default. Pass `archived: false` to un-archive.
 *
 * Routed through the `set_project_archived` SECURITY DEFINER RPC
 * (migration 32) so any project member can flip the flag — the
 * direct UPDATE policy on `public.projects` is still creator-only,
 * which keeps non-owner edits to other metadata (rename, etc.)
 * locked down.
 */
export function useArchiveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; archived: boolean }) => {
      const { data, error } = await supabase.rpc('set_project_archived', {
        p_project_id: input.id,
        p_archived: input.archived,
      });
      if (error) throw error;
      return (data ?? [])[0] ?? null;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

/**
 * Hard-delete a project. RLS restricts this to the creator or
 * super_admin; the FK cascade on tasks + project_members wipes the
 * dependent rows automatically. Use sparingly — there's no undo.
 * Prefer `useArchiveProject` when the operator just wants to clear
 * the row out of the navigation list.
 */
export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Sidebar folders (migration 86)
//
// Org-shared tree used to group projects in the navigation. Folders are
// structure only — project visibility inside a folder is still whatever
// the projects RLS says, so two users can see different contents for
// the same folder.
// ---------------------------------------------------------------------------

export function useProjectFolders() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: projectKeys.folders(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_folders')
        .select('id, organization_id, name, parent_id, created_by, created_at, updated_at')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ProjectFolder[];
    },
  });
}

export function useCreateProjectFolder() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { name: string; parentId?: string | null }) => {
      if (!profile) throw new Error('Not signed in.');
      const name = input.name.trim();
      if (!name) throw new Error('Folder name is required.');
      const payload: Inserts<'project_folders'> = {
        organization_id: profile.organization_id,
        name,
        parent_id: input.parentId ?? null,
        created_by: profile.id,
      };
      const { data, error } = await supabase
        .from('project_folders')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data as ProjectFolder;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.folders() });
    },
  });
}

export function useRenameProjectFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name: string }) => {
      const name = input.name.trim();
      if (!name) throw new Error('Folder name is required.');
      const { data, error } = await supabase
        .from('project_folders')
        .update({ name })
        .eq('id', input.id)
        .select()
        .single();
      if (error) throw error;
      return data as ProjectFolder;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.folders() });
    },
  });
}

/**
 * Delete a folder via the `delete_project_folder` RPC, which re-parents
 * the folder's sub-folders and projects to its parent first — contents
 * move up one level instead of disappearing.
 */
export function useDeleteProjectFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { error } = await supabase.rpc('delete_project_folder', {
        p_folder_id: input.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.folders() });
      // Projects inside the folder were re-parented server-side.
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

/**
 * Move a project into a folder (folderId = null moves it to the top
 * level), optionally also writing a new ordering for the destination
 * list. Two RPC round-trips (`set_project_folder` then
 * `set_project_positions`) — both SECURITY DEFINER so any project
 * member can organize the sidebar tree.
 *
 * Optimistically rewrites every cached project list (same approach as
 * `useReorderProjects`) so the row jumps into its folder the instant
 * the drop / menu action completes.
 */
export function useMoveProjectToFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      projectId: string;
      folderId: string | null;
      /** Desired ordering of the DESTINATION list, including the moved project. */
      orderedIds?: string[];
    }) => {
      const { error } = await supabase.rpc('set_project_folder', {
        p_project_id: input.projectId,
        p_folder_id: input.folderId,
      });
      if (error) throw error;
      if (input.orderedIds && input.orderedIds.length > 0) {
        const positions = input.orderedIds.map((_, idx) => idx + 1);
        const { error: posErr } = await supabase.rpc('set_project_positions', {
          p_ids: input.orderedIds,
          p_positions: positions,
        });
        if (posErr) throw posErr;
      }
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: projectKeys.lists() });

      const order = new Map(
        (input.orderedIds ?? []).map((id, idx) => [id, idx + 1]),
      );
      const snapshots: Array<{
        key: ReturnType<typeof projectKeys.list>;
        previous: ProjectWithMembers[] | undefined;
      }> = [];

      // Same prefix-matching caveat as useReorderProjects: the summary
      // cache stores a Map, so skip anything that isn't an array.
      const lists = qc.getQueriesData<unknown>({
        queryKey: projectKeys.lists(),
      });
      for (const [key, value] of lists) {
        if (!Array.isArray(value)) continue;
        const arr = value as ProjectWithMembers[];
        snapshots.push({
          key: key as ReturnType<typeof projectKeys.list>,
          previous: arr,
        });
        const next = arr
          .map((p) => {
            const patched =
              p.id === input.projectId ? { ...p, folder_id: input.folderId } : p;
            const pos = order.get(patched.id);
            return pos !== undefined ? { ...patched, position: pos } : patched;
          })
          .sort((a, b) => a.position - b.position);
        qc.setQueryData(key, next);
      }
      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      for (const snap of ctx?.snapshots ?? []) {
        qc.setQueryData(snap.key, snap.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

export function useAddProjectMembers() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { projectId: string; profileIds: string[] }) => {
      if (!profile) throw new Error('Not signed in.');
      if (input.profileIds.length === 0) return [];
      const rows: Inserts<'project_members'>[] = input.profileIds.map((id) => ({
        project_id: input.projectId,
        profile_id: id,
        organization_id: profile.organization_id,
        added_by: profile.id,
      }));
      const { data, error } = await supabase
        .from('project_members')
        .upsert(rows, { onConflict: 'project_id,profile_id' })
        .select();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.detail(vars.projectId) });
      void qc.invalidateQueries({ queryKey: projectKeys.members(vars.projectId) });
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

export function useRemoveProjectMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; profileId: string }) => {
      const { error } = await supabase
        .from('project_members')
        .delete()
        .eq('project_id', input.projectId)
        .eq('profile_id', input.profileId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.detail(vars.projectId) });
      void qc.invalidateQueries({ queryKey: projectKeys.members(vars.projectId) });
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Sections
//
// Named groupings inside a project (migration 50). A task with a NULL
// `section_id` lives in the implicit "(no section)" bucket and the UI
// renders it at the top of the project table. Subtasks inherit their
// parent's section implicitly — the hooks here only manage the
// section list itself, not membership (that's a column on the task
// row, written through useUpdateTask).
// ---------------------------------------------------------------------------

const SECTION_SELECT =
  'id, organization_id, project_id, name, position, created_by, created_at, updated_at';

export function useProjectSections(projectId: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: projectId
      ? projectKeys.sections(projectId)
      : ['projects', 'sections', 'noop'],
    enabled: !!profile && !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_sections')
        .select(SECTION_SELECT)
        .eq('project_id', projectId!)
        .order('position', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ProjectSection[];
    },
  });
}

export function useCreateProjectSection() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { projectId: string; name: string }) => {
      if (!profile) throw new Error('Not signed in.');
      const name = input.name.trim();
      if (!name) throw new Error('Section name is required.');
      const payload: Inserts<'project_sections'> = {
        organization_id: profile.organization_id,
        project_id: input.projectId,
        name,
        created_by: profile.id,
      };
      const { data, error } = await supabase
        .from('project_sections')
        .insert(payload)
        .select(SECTION_SELECT)
        .single();
      if (error) throw error;
      return data as ProjectSection;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.sections(vars.projectId) });
    },
  });
}

export function useUpdateProjectSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      projectId: string;
      patch: Updates<'project_sections'>;
    }) => {
      const { data, error } = await supabase
        .from('project_sections')
        .update(input.patch)
        .eq('id', input.id)
        .select(SECTION_SELECT)
        .single();
      if (error) throw error;
      return data as ProjectSection;
    },
    onMutate: async (input) => {
      const key = projectKeys.sections(input.projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ProjectSection[]>(key);
      if (previous) {
        qc.setQueryData<ProjectSection[]>(
          key,
          previous.map((s) =>
            s.id === input.id ? ({ ...s, ...input.patch } as ProjectSection) : s,
          ),
        );
      }
      return { previous, key };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous && ctx.key) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.sections(vars.projectId) });
    },
  });
}

/**
 * Hard-delete a section. The FK on `tasks.section_id` is ON DELETE SET
 * NULL, so tasks in the section don't disappear — they fall back into
 * the "(no section)" bucket at the top of the table. We invalidate
 * both the sections list and the tasks list so the UI re-groups.
 */
export function useDeleteProjectSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; projectId: string }) => {
      const { error } = await supabase
        .from('project_sections')
        .delete()
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.sections(vars.projectId) });
      void qc.invalidateQueries({ queryKey: projectKeys.tasks(vars.projectId) });
    },
  });
}

/**
 * Persist a new ordering for the sections inside a single project. The
 * caller passes the full list of section ids in their desired display
 * order; we generate 1-based positions for the batch and ship them to
 * the `set_project_section_positions` RPC (migration 50).
 *
 * Mirrors `useReorderProjects` for projects: cancel + snapshot +
 * optimistic rewrite + invalidate so the table re-orders the instant
 * the user clicks the up/down arrows on a section header.
 */
export function useReorderProjectSections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; orderedIds: string[] }) => {
      if (input.orderedIds.length === 0) return;
      const positions = input.orderedIds.map((_, idx) => idx + 1);
      const { error } = await supabase.rpc('set_project_section_positions', {
        p_ids: input.orderedIds,
        p_positions: positions,
      });
      if (error) throw error;
    },
    onMutate: async (input) => {
      const key = projectKeys.sections(input.projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ProjectSection[]>(key);
      if (previous) {
        const order = new Map(input.orderedIds.map((id, idx) => [id, idx + 1]));
        const next = previous
          .map((s) => {
            const pos = order.get(s.id);
            return pos !== undefined ? { ...s, position: pos } : s;
          })
          .sort((a, b) => a.position - b.position);
        qc.setQueryData<ProjectSection[]>(key, next);
      }
      return { previous, key };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous && ctx.key) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.sections(vars.projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

/**
 * Postgrest column list for `tasks` rows that the UI renders. Includes
 * the `task_assignees` embed (qualified by the FK constraint name so
 * Postgrest can disambiguate the join) so every task arrives with its
 * full assignee profile list. Centralised so the create / update
 * mutations and the list query can't drift out of sync.
 *
 * The embed shape is:
 *   task_assignees: [{ profile_id, profiles: { id, full_name, email, avatar_url } }, ...]
 * which `mapTaskRow` collapses to `assignees: TaskAssigneeProfile[]`.
 */
const TASK_SELECT =
  'id, organization_id, project_id, name, description, due_date, approximate_workload_hours, priority, status, is_completed, completed_at, position, section_id, parent_task_id, created_by, created_at, updated_at, project:projects!tasks_project_id_fkey(id, name), task_assignees(profile_id, profiles!task_assignees_profile_id_fkey(id, full_name, email, avatar_url))';

interface RawTaskRow extends Task {
  project?: Pick<Tables<'projects'>, 'id' | 'name'> | null;
  task_assignees: Array<{
    profile_id: string;
    profiles: TaskAssigneeProfile | null;
  }> | null;
}

/**
 * Matches every per-project tasks cache (`projectKeys.tasks(id)` =
 * ['projects', id, 'tasks']). With multi-homed tasks (migration 86)
 * the same task row can sit in SEVERAL project caches — its home
 * project's and every linked project's — so task mutations patch /
 * invalidate across all of them instead of just the caller's
 * `projectId`.
 */
function isTaskListKey(key: readonly unknown[]): boolean {
  return key.length === 3 && key[0] === 'projects' && key[2] === 'tasks';
}

function mapTaskRow(row: RawTaskRow): TaskWithAssignees {
  const assignees: TaskAssigneeProfile[] = [];
  for (const link of row.task_assignees ?? []) {
    if (link.profiles) assignees.push(link.profiles);
  }
  // Stable display order: alphabetical by name (falling back to email)
  // so the avatar stack on a row doesn't flicker between renders just
  // because Postgrest returned the embed in a different order.
  assignees.sort((a, b) =>
    (a.full_name ?? a.email).localeCompare(b.full_name ?? b.email),
  );
  const { task_assignees: _drop, ...rest } = row;
  return {
    ...(rest as Task),
    // numeric columns can arrive as strings from Postgrest.
    approximate_workload_hours: coerceWorkloadHours(
      rest.approximate_workload_hours,
    ),
    assignees,
  };
}

/**
 * Every task that shows on a project's detail page:
 *   1. tasks whose HOME is this project (`tasks.project_id`), and
 *   2. tasks LINKED into this project via task_project_links
 *      (migration 86) — plus their subtasks, so the expandable
 *      subtask groups and the drawer's subtask list keep working
 *      from the linked side.
 *
 * Linked rows keep their home `project_id` (that's how the UI tells
 * them apart: `task.project_id !== pageProjectId`) but their
 * `section_id` is cleared in this projection — sections are
 * project-scoped and a foreign section id would otherwise confuse the
 * grouping. They render at the bottom of the unsectioned bucket.
 */
export function useTasks(projectId: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: projectId ? projectKeys.tasks(projectId) : ['projects', 'tasks', 'noop'],
    enabled: !!profile && !!projectId,
    queryFn: async () => {
      const [own, links] = await Promise.all([
        supabase
          .from('tasks')
          .select(TASK_SELECT)
          .eq('project_id', projectId!)
          .order('is_completed', { ascending: true })
          .order('position', { ascending: true })
          .order('created_at', { ascending: true }),
        supabase
          .from('task_project_links')
          .select(`task_id, task:tasks(${TASK_SELECT})`)
          .eq('project_id', projectId!),
      ]);
      if (own.error) throw own.error;
      if (links.error) throw links.error;

      const ownTasks = ((own.data ?? []) as unknown as RawTaskRow[]).map(
        mapTaskRow,
      );

      const linkedParents = ((links.data ?? []) as unknown as Array<{
        task_id: string;
        task: RawTaskRow | null;
      }>)
        .map((l) => l.task)
        .filter((t): t is RawTaskRow => !!t);

      if (linkedParents.length === 0) return ownTasks;

      // Subtasks of linked parents live in the parent's home project,
      // so the first query didn't pick them up — fetch them explicitly.
      const { data: childData, error: childErr } = await supabase
        .from('tasks')
        .select(TASK_SELECT)
        .in(
          'parent_task_id',
          linkedParents.map((t) => t.id),
        )
        .order('position', { ascending: true })
        .order('created_at', { ascending: true });
      if (childErr) throw childErr;

      const sortedParents = linkedParents
        .map(mapTaskRow)
        .sort(
          (a, b) =>
            Number(a.is_completed) - Number(b.is_completed) ||
            a.position - b.position ||
            a.created_at.localeCompare(b.created_at),
        );
      const linkedChildren = ((childData ?? []) as unknown as RawTaskRow[]).map(
        mapTaskRow,
      );

      const linked = [...sortedParents, ...linkedChildren].map((t) => ({
        ...t,
        section_id: null,
      }));
      return [...ownTasks, ...linked];
    },
  });
}

/**
 * The extra projects a single task is linked into (its home project is
 * `task.project_id` and is not part of this list). Powers the
 * "Projects" row in the task detail drawer.
 */
export function useTaskProjectLinks(taskId: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: taskId ? taskKeys.projects(taskId) : ['tasks', 'projects', 'noop'],
    enabled: !!profile && !!taskId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_project_links')
        .select('project_id, created_at, project:projects(id, name, archived_at)')
        .eq('task_id', taskId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as TaskProjectLink[];
    },
  });
}

/**
 * Move ("re-home") a task into another project via the
 * move_task_to_project RPC (migration 86). The task leaves this
 * project's list entirely — section/parent cleared, appended to the
 * destination's unsectioned bucket, subtasks travel along — so we
 * invalidate the task caches on BOTH sides plus the dashboard counts.
 */
export function useMoveTaskToProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      taskId: string;
      fromProjectId: string;
      toProjectId: string;
    }) => {
      const { error } = await supabase.rpc('move_task_to_project', {
        p_task_id: input.taskId,
        p_project_id: input.toProjectId,
      });
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      // Not just source + destination: the task may also be linked
      // into third projects whose cached rows embed the (now stale)
      // home-project badge, so refresh every task list cache.
      void qc.invalidateQueries({ predicate: (q) => isTaskListKey(q.queryKey) });
      void qc.invalidateQueries({ queryKey: taskKeys.projects(vars.taskId) });
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

/**
 * Link a task into an additional project (multi-homing). The task
 * stays put in its home project and additionally appears in the
 * target project's unsectioned bucket. The DB rejects duplicates
 * (PK), self-links (home project), and subtask links via the
 * validation trigger; we re-shape the duplicate error into a
 * friendlier message because it's the only one a user can plausibly
 * hit from the UI.
 */
export function useAddTaskProjectLink() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { taskId: string; projectId: string }) => {
      if (!profile) throw new Error('Not signed in.');
      const payload: Inserts<'task_project_links'> = {
        task_id: input.taskId,
        project_id: input.projectId,
        organization_id: profile.organization_id,
        added_by: profile.id,
      };
      const { error } = await supabase
        .from('task_project_links')
        .insert(payload);
      if (error) {
        if (error.code === '23505') {
          throw new Error('This task is already in that project.');
        }
        throw error;
      }
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.tasks(vars.projectId) });
      void qc.invalidateQueries({ queryKey: taskKeys.projects(vars.taskId) });
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

/** Remove a task from a project it was linked into (home is untouched). */
export function useRemoveTaskProjectLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { taskId: string; projectId: string }) => {
      const { error } = await supabase
        .from('task_project_links')
        .delete()
        .eq('task_id', input.taskId)
        .eq('project_id', input.projectId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.tasks(vars.projectId) });
      void qc.invalidateQueries({ queryKey: taskKeys.projects(vars.taskId) });
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

export interface CreateTaskInput {
  projectId: string;
  name: string;
  description?: string | null;
  /**
   * Profile ids to assign to the task on creation. Pass an empty array
   * (or omit) for an unassigned task; pass any number of ids to attach
   * the full set in one round-trip.
   */
  assigneeIds?: string[];
  dueDate?: string | null;
  /** Optional effort estimate in hours (migration 99). */
  approximateWorkloadHours?: number | null;
  priority?: TaskPriority;
  status?: TaskStatus;
  /**
   * Optional section to drop the new task into (migration 50). Omit /
   * null for the "(no section)" bucket. Ignored when `parentTaskId` is
   * set — subtasks inherit their parent's section implicitly.
   */
  sectionId?: string | null;
  /**
   * When set, the new row is created as a subtask of the referenced
   * task. The parent task's project_id is reused server-side; we still
   * pass `projectId` for consistency and to satisfy the NOT NULL
   * column.
   */
  parentTaskId?: string | null;
}

export function useCreateTask() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateTaskInput) => {
      if (!profile) throw new Error('Not signed in.');
      const name = input.name.trim();
      if (!name) throw new Error('Task name is required.');
      const payload: Inserts<'tasks'> = {
        organization_id: profile.organization_id,
        project_id: input.projectId,
        name,
        description: input.description ?? null,
        due_date: input.dueDate ?? null,
        approximate_workload_hours: input.approximateWorkloadHours ?? null,
        priority: input.priority ?? 'medium',
        status: input.status ?? 'not_started',
        // Subtasks don't carry their own section — the application
        // groups them under their parent. We deliberately null
        // section_id when parent is set so the column doesn't drift
        // out of sync with the parent's section after a move.
        section_id: input.parentTaskId ? null : input.sectionId ?? null,
        parent_task_id: input.parentTaskId ?? null,
        created_by: profile.id,
      };

      // Step 1: create the task row. We do this without the assignee
      // embed first because the join table rows don't exist yet and a
      // single round-trip with embed would just return an empty array.
      const { data: createdRaw, error: insertErr } = await supabase
        .from('tasks')
        .insert(payload)
        .select('id')
        .single();
      if (insertErr) throw insertErr;
      const createdId = (createdRaw as { id: string }).id;

      // Step 2: attach assignees (if any). Many-to-many lives in
      // `task_assignees` (migration 35); we deduplicate on the client
      // and rely on the table's PK (task_id, profile_id) for safety.
      const uniqueAssignees = Array.from(
        new Set((input.assigneeIds ?? []).filter(Boolean)),
      );
      if (uniqueAssignees.length > 0) {
        const assigneeRows: Inserts<'task_assignees'>[] = uniqueAssignees.map(
          (profileId) => ({
            task_id: createdId,
            profile_id: profileId,
            organization_id: profile.organization_id,
            assigned_by: profile.id,
          }),
        );
        const { error: assigneesErr } = await supabase
          .from('task_assignees')
          .insert(assigneeRows);
        if (assigneesErr) {
          // The task row is good; bubble the assignee failure so the
          // UI can surface a "task created but couldn't assign X"
          // toast. The user can then re-pick assignees from the row
          // dropdown which routes through useSetTaskAssignees.
          throw new Error(
            `Task created but failed to assign everyone: ${assigneesErr.message}`,
          );
        }
      }

      // Step 3: refetch with the embed so the cache lands a complete
      // row (including the freshly-created assignees) — keeps the
      // optimistic-update pipeline simple.
      const { data: refreshed, error: refreshErr } = await supabase
        .from('tasks')
        .select(TASK_SELECT)
        .eq('id', createdId)
        .single();
      if (refreshErr) throw refreshErr;
      return mapTaskRow(refreshed as unknown as RawTaskRow);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.tasks(vars.projectId) });
      // A new subtask under a multi-homed parent must also show up on
      // every linked project's page — those caches key off OTHER
      // project ids, so refresh them all when a parent is involved.
      if (vars.parentTaskId) {
        void qc.invalidateQueries({
          predicate: (q) => isTaskListKey(q.queryKey),
        });
      }
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
      void qc.invalidateQueries({ queryKey: [...taskKeys.all, 'mine'] });
      void qc.invalidateQueries({ queryKey: ['calendar', 'my-tasks'] });
    },
  });
}

/**
 * Generic patch on a task. The detail-page grid uses this for inline
 * edits (rename, change due date, change priority, change status).
 * is_completed is also routed through here so the checkbox benefits
 * from the same optimistic-update pipeline.
 *
 * NOTE: Assignees are NOT updated through this hook — they live on a
 * separate join table now (see migration 35). Use `useSetTaskAssignees`
 * for assignment changes.
 */
export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      projectId: string;
      patch: Updates<'tasks'>;
    }) => {
      const { data, error } = await supabase
        .from('tasks')
        .update(input.patch)
        .eq('id', input.id)
        .select(TASK_SELECT)
        .single();
      if (error) throw error;
      return mapTaskRow(data as unknown as RawTaskRow);
    },
    // Optimistic update: write the patched row into EVERY project
    // tasks cache that holds it (a multi-homed task appears in its
    // home cache and every linked project's cache) so toggles +
    // dropdowns feel instant wherever the edit happens. We snapshot
    // the prior values so the error handler can roll back.
    onMutate: async (input) => {
      await qc.cancelQueries({
        predicate: (q) => isTaskListKey(q.queryKey),
      });
      const snapshots = qc
        .getQueriesData<TaskWithAssignees[]>({
          predicate: (q) => isTaskListKey(q.queryKey),
        })
        .filter(([, value]) => !!value?.some((t) => t.id === input.id));
      for (const [key, value] of snapshots) {
        qc.setQueryData<TaskWithAssignees[]>(
          key,
          value!.map((t) =>
            t.id === input.id ? ({ ...t, ...input.patch } as TaskWithAssignees) : t,
          ),
        );
      }
      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      for (const [key, value] of ctx?.snapshots ?? []) {
        qc.setQueryData(key, value);
      }
    },
    onSettled: () => {
      // Reconcile every project tasks cache we may have optimistically
      // patched — the row can live in several (home + linked pages).
      // Only actively-mounted queries refetch; the rest just go stale.
      void qc.invalidateQueries({ predicate: (q) => isTaskListKey(q.queryKey) });
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
      void qc.invalidateQueries({ queryKey: [...taskKeys.all, 'mine'] });
      void qc.invalidateQueries({ queryKey: ['calendar', 'my-tasks'] });
    },
  });
}

/**
 * Replace the assignee set on a single task. Computes the diff against
 * the cached value (so we only INSERT the new ones and DELETE the ones
 * that were dropped, instead of clearing + re-inserting and burning
 * audit-log rows for unchanged links). Optimistic — the local cache
 * is updated to the desired set immediately and rolled back on error.
 *
 * Pass an empty array to un-assign everybody. Profile ids that aren't
 * already in the org will be rejected by the table's FK.
 */
export function useSetTaskAssignees() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      taskId: string;
      projectId: string;
      profileIds: string[];
      currentProfileIds: string[];
    }) => {
      if (!profile) throw new Error('Not signed in.');
      const desired = Array.from(new Set(input.profileIds.filter(Boolean)));
      const current = new Set(input.currentProfileIds);
      const desiredSet = new Set(desired);
      const toAdd = desired.filter((id) => !current.has(id));
      const toRemove = input.currentProfileIds.filter((id) => !desiredSet.has(id));

      // Run the two writes in parallel — they're independent and the
      // unique PK on (task_id, profile_id) keeps things safe even if
      // they race. Wrapped in async fns because the supabase query
      // builder is `PromiseLike` (not a true `Promise`), which trips
      // up the typing on `Promise.all` directly.
      const ops: Promise<void>[] = [];
      if (toAdd.length > 0) {
        const rows: Inserts<'task_assignees'>[] = toAdd.map((profileId) => ({
          task_id: input.taskId,
          profile_id: profileId,
          organization_id: profile.organization_id,
          assigned_by: profile.id,
        }));
        ops.push(
          (async () => {
            const { error } = await supabase
              .from('task_assignees')
              .upsert(rows, { onConflict: 'task_id,profile_id' });
            if (error) throw error;
          })(),
        );
      }
      if (toRemove.length > 0) {
        ops.push(
          (async () => {
            const { error } = await supabase
              .from('task_assignees')
              .delete()
              .eq('task_id', input.taskId)
              .in('profile_id', toRemove);
            if (error) throw error;
          })(),
        );
      }
      await Promise.all(ops);
    },
    onMutate: async (input) => {
      // Patch every project tasks cache holding the row — a
      // multi-homed task is cached under its home project AND every
      // linked project it appears in.
      await qc.cancelQueries({
        predicate: (q) => isTaskListKey(q.queryKey),
      });
      const snapshots = qc
        .getQueriesData<TaskWithAssignees[]>({
          predicate: (q) => isTaskListKey(q.queryKey),
        })
        .filter(([, value]) => !!value?.some((t) => t.id === input.taskId));
      for (const [key, previous] of snapshots) {
        const desired = new Set(input.profileIds);
        qc.setQueryData<TaskWithAssignees[]>(
          key,
          previous!.map((t) => {
            if (t.id !== input.taskId) return t;
            // Keep only the assignees that are still wanted, then
            // append placeholder entries for any new ones we don't
            // have profile data for in the cache. The next
            // invalidate will replace these with the real rows.
            const kept = t.assignees.filter((a) => desired.has(a.id));
            const haveIds = new Set(kept.map((a) => a.id));
            const placeholders: TaskAssigneeProfile[] = [];
            for (const id of input.profileIds) {
              if (haveIds.has(id)) continue;
              const seed = previous!
                .flatMap((row) => row.assignees)
                .find((a) => a.id === id);
              placeholders.push(
                seed ?? { id, full_name: null, email: '…', avatar_url: null },
              );
            }
            return { ...t, assignees: [...kept, ...placeholders] };
          }),
        );
      }
      return { snapshots };
    },
    onError: (_err, _input, ctx) => {
      for (const [key, value] of ctx?.snapshots ?? []) {
        qc.setQueryData(key, value);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ predicate: (q) => isTaskListKey(q.queryKey) });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; projectId: string }) => {
      const { error } = await supabase.from('tasks').delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      // The deleted row may have been multi-homed into other projects'
      // caches — invalidate them all, not just the caller's.
      void qc.invalidateQueries({ predicate: (q) => isTaskListKey(q.queryKey) });
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

/**
 * Batch-rewrite (section_id, parent_task_id, position) on a set of
 * tasks. The detail page uses this for drag-and-drop reorder: a
 * single drop can shift many rows (the moved task plus everything in
 * its source + destination buckets that needs to renumber), so we
 * ship the full diff in one call to `set_task_positions`
 * (migration 53) instead of fanning out per-row updateTask writes.
 *
 * Each `moves` entry is a full target snapshot for that task —
 * `section_id` / `parent_task_id` may be the same as the current
 * value (within-bucket reorder) or different (cross-bucket /
 * subtask-promote / subtask-demote). The optimistic update writes
 * every move into the tasks cache up front so the table re-buckets
 * the instant the drop completes; an error rolls the cache back to
 * its pre-drop snapshot.
 */
export interface TaskMove {
  id: string;
  section_id: string | null;
  parent_task_id: string | null;
  position: number;
}

export function useReorderTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; moves: TaskMove[] }) => {
      if (input.moves.length === 0) return;
      const ids = input.moves.map((m) => m.id);
      const sectionIds = input.moves.map((m) => m.section_id);
      const parentIds = input.moves.map((m) => m.parent_task_id);
      const positions = input.moves.map((m) => m.position);
      const { error } = await supabase.rpc('set_task_positions', {
        p_ids: ids,
        p_section_ids: sectionIds,
        p_parent_task_ids: parentIds,
        p_positions: positions,
      });
      if (error) throw error;
    },
    onMutate: async (input) => {
      const key = projectKeys.tasks(input.projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<TaskWithAssignees[]>(key);
      if (previous) {
        const patchById = new Map<string, TaskMove>();
        for (const m of input.moves) patchById.set(m.id, m);
        const next = previous.map((t) => {
          const m = patchById.get(t.id);
          if (!m) return t;
          return {
            ...t,
            section_id: m.section_id,
            parent_task_id: m.parent_task_id,
            position: m.position,
          };
        });
        qc.setQueryData<TaskWithAssignees[]>(key, next);
      }
      return { previous, key };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous && ctx.key) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: projectKeys.tasks(vars.projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// "My tasks" — the cross-project queue on the Tasks page
// ---------------------------------------------------------------------------

/**
 * A task assigned to the signed-in user, joined with the minimal
 * project fields the My tasks page needs to label which project (and
 * department) it belongs to.
 *
 * Deliberately lighter than `TaskWithAssignees`: the page renders a
 * cross-project queue, so it doesn't need the full assignee list (the
 * current user is by definition one of them) or task descriptions.
 */
export type MyTaskRow = Pick<
  Task,
  | 'id'
  | 'name'
  | 'due_date'
  | 'approximate_workload_hours'
  | 'priority'
  | 'status'
  | 'is_completed'
> & {
  project: Pick<
    Tables<'projects'>,
    'id' | 'name' | 'archived_at' | 'department'
  >;
};

interface RawMyTaskRow extends Pick<
  Task,
  | 'id'
  | 'name'
  | 'due_date'
  | 'approximate_workload_hours'
  | 'priority'
  | 'status'
  | 'is_completed'
> {
  project: Pick<
    Tables<'projects'>,
    'id' | 'name' | 'archived_at' | 'department'
  > | null;
  task_assignees: Array<{ profile_id: string }> | null;
}

// Safety cap on the My tasks fetch. Well above any realistic personal
// queue; it only exists so a runaway assignment script can't pull the
// whole tasks table into the browser.
const MY_TASKS_FETCH_LIMIT = 500;

/**
 * Every open task assigned to the current user across the active
 * projects they can see, ordered by due_date ascending with undated
 * tasks last — overdue first (oldest overdue at the top), then the
 * soonest-due upcoming, then the backlog without a date.
 *
 * Powers the My tasks page: the Tasks section is the hub that links
 * every department's projects together, so the operator lands on
 * their whole queue without having to walk each project.
 *
 * Filters applied:
 *   * `is_completed = false`            — only open work
 *   * `task_assignees.profile_id = me`  — assigned to current user
 *   * archived projects are dropped     — see below
 *
 * The archived-project filter runs client-side because Postgrest
 * doesn't apply `is.null` against an embedded relation reliably with
 * an `!inner` parent join — the inner join returns the row regardless
 * of the embedded column predicate. Doing it in JS after the fetch
 * keeps the predicate simple and matches the behaviour of the
 * project list, which also splits archived/active client-side.
 */
export function useMyOpenTasks() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: [...taskKeys.all, 'mine', 'open', profile?.id ?? 'noop'],
    enabled: !!profile,
    queryFn: async () => {
      // `task_assignees!inner(...)` plus `.eq('task_assignees.profile_id', me)`
      // walks the join table and only returns tasks where AT LEAST ONE
      // assignee row matches the current user — i.e. tasks assigned to
      // me.
      const { data, error } = await supabase
        .from('tasks')
        .select(
          'id, name, due_date, approximate_workload_hours, priority, status, is_completed, project:projects!tasks_project_id_fkey!inner(id, name, archived_at, department), task_assignees!inner(profile_id)',
        )
        .eq('task_assignees.profile_id', profile!.id)
        .eq('is_completed', false)
        .order('due_date', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true })
        .limit(MY_TASKS_FETCH_LIMIT);
      if (error) throw error;
      const rows = (data ?? []) as unknown as RawMyTaskRow[];
      const out: MyTaskRow[] = [];
      for (const r of rows) {
        if (!r.project || r.project.archived_at) continue;
        out.push({
          id: r.id,
          name: r.name,
          due_date: r.due_date,
          approximate_workload_hours: coerceWorkloadHours(
            r.approximate_workload_hours,
          ),
          priority: r.priority,
          status: r.status,
          is_completed: r.is_completed,
          project: r.project,
        });
      }
      return out;
    },
  });
}

/**
 * Open work assigned to the current user that is due this local week
 * (Monday–Sunday). Used by the dashboard to flag a day or week that
 * is already over a realistic 8h / 40h capacity.
 *
 * Same join + archived-project filter as `useMyOpenTasks`, but scoped
 * to the week window so it stays cheap to poll.
 */
export type MyWorkloadSlice = {
  todayHours: number;
  weekHours: number;
};

export function useMyWorkloadThisWeek() {
  const { profile } = useAuth();
  const weekStart = localDateKey(startOfLocalWeekMonday());
  const weekEnd = localDateKey(endOfLocalWeekSunday());
  const today = localDateKey();
  return useQuery({
    queryKey: [
      ...taskKeys.all,
      'mine',
      'workload-week',
      profile?.id ?? 'noop',
      weekStart,
      today,
    ],
    enabled: !!profile,
    queryFn: async (): Promise<MyWorkloadSlice> => {
      const { data, error } = await supabase
        .from('tasks')
        .select(
          'due_date, approximate_workload_hours, project:projects!tasks_project_id_fkey!inner(archived_at), task_assignees!inner(profile_id)',
        )
        .eq('task_assignees.profile_id', profile!.id)
        .eq('is_completed', false)
        .gte('due_date', weekStart)
        .lte('due_date', weekEnd);
      if (error) throw error;
      type Raw = {
        due_date: string | null;
        approximate_workload_hours: unknown;
        project: { archived_at: string | null } | null;
      };
      let todayHours = 0;
      let weekHours = 0;
      for (const r of (data ?? []) as unknown as Raw[]) {
        if (!r.project || r.project.archived_at) continue;
        const hours = coerceWorkloadHours(r.approximate_workload_hours) ?? 0;
        if (hours <= 0) continue;
        weekHours += hours;
        if (r.due_date === today) todayHours += hours;
      }
      return {
        todayHours: Math.round(todayHours * 100) / 100,
        weekHours: Math.round(weekHours * 100) / 100,
      };
    },
  });
}

// ---------------------------------------------------------------------------
// Task comments
//
// Discussion thread shown in the task detail drawer. Comments are
// scoped to a single task and visible to every project member (RLS in
// migration 34). Reads/writes invalidate `taskKeys.comments(taskId)`;
// we don't touch `projectKeys` here because the table view doesn't
// surface comment counts (yet).
// ---------------------------------------------------------------------------

export function useTaskComments(taskId: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: taskId ? taskKeys.comments(taskId) : ['tasks', 'comments', 'noop'],
    enabled: !!profile && !!taskId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_comments')
        .select(
          'id, organization_id, task_id, author_id, body, created_at, updated_at, author:author_id(id, full_name, email, avatar_url)',
        )
        .eq('task_id', taskId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as TaskCommentWithAuthor[];
    },
  });
}

export function useCreateTaskComment() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { taskId: string; body: string }) => {
      if (!profile) throw new Error('Not signed in.');
      const body = input.body.trim();
      if (!body) throw new Error('Comment cannot be empty.');
      const payload: Inserts<'task_comments'> = {
        organization_id: profile.organization_id,
        task_id: input.taskId,
        author_id: profile.id,
        body,
      };
      const { data, error } = await supabase
        .from('task_comments')
        .insert(payload)
        .select(
          'id, organization_id, task_id, author_id, body, created_at, updated_at, author:author_id(id, full_name, email, avatar_url)',
        )
        .single();
      if (error) throw error;
      return data as unknown as TaskCommentWithAuthor;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: taskKeys.comments(vars.taskId) });
    },
  });
}

export function useUpdateTaskComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; taskId: string; body: string }) => {
      const body = input.body.trim();
      if (!body) throw new Error('Comment cannot be empty.');
      const { data, error } = await supabase
        .from('task_comments')
        .update({ body })
        .eq('id', input.id)
        .select(
          'id, organization_id, task_id, author_id, body, created_at, updated_at, author:author_id(id, full_name, email, avatar_url)',
        )
        .single();
      if (error) throw error;
      return data as unknown as TaskCommentWithAuthor;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: taskKeys.comments(vars.taskId) });
    },
  });
}

export function useDeleteTaskComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; taskId: string }) => {
      const { error } = await supabase
        .from('task_comments')
        .delete()
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: taskKeys.comments(vars.taskId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Task attachments
//
// Files uploaded against a task. Bytes live in the `task-attachments`
// Storage bucket under `<org>/<project>/<task>/<filename>`; the
// task_attachments table stores the pointer + denormalised file info
// so we can render the list without round-tripping Storage.
//
// Upload flow:
//   1. PUT the bytes to Storage.
//   2. INSERT the metadata row.
//   3. Storage cleanup on row delete is best-effort (mirrors the
//      receipts hooks pattern).
// ---------------------------------------------------------------------------

export function useTaskAttachments(taskId: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: taskId
      ? taskKeys.attachments(taskId)
      : ['tasks', 'attachments', 'noop'],
    enabled: !!profile && !!taskId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_attachments')
        .select(
          'id, organization_id, task_id, uploaded_by, storage_path, file_name, mime_type, size_bytes, created_at, uploader:uploaded_by(id, full_name, email, avatar_url)',
        )
        .eq('task_id', taskId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as TaskAttachmentWithUploader[];
    },
  });
}

function buildTaskAttachmentPath(args: {
  organization_id: string;
  project_id: string;
  task_id: string;
  filename: string;
}): string {
  // Sanitise the filename so the storage path stays safe and unique.
  // Prepend a timestamp so two uploads with the same name don't
  // collide and so the "most recent" sort survives without us also
  // storing it in a separate column.
  const stamp = Date.now();
  const cleaned = args.filename.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `${args.organization_id}/${args.project_id}/${args.task_id}/${stamp}-${cleaned}`;
}

export function useUploadTaskAttachment() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      taskId: string;
      projectId: string;
      file: File;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const path = buildTaskAttachmentPath({
        organization_id: profile.organization_id,
        project_id: input.projectId,
        task_id: input.taskId,
        filename: input.file.name,
      });

      const { error: uploadErr } = await supabase.storage
        .from(TASK_ATTACHMENTS_BUCKET)
        .upload(path, input.file, {
          upsert: false,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      const payload: Inserts<'task_attachments'> = {
        organization_id: profile.organization_id,
        task_id: input.taskId,
        uploaded_by: profile.id,
        storage_path: path,
        file_name: input.file.name,
        mime_type: input.file.type || null,
        size_bytes: input.file.size,
      };
      const { data, error: dbErr } = await supabase
        .from('task_attachments')
        .insert(payload)
        .select(
          'id, organization_id, task_id, uploaded_by, storage_path, file_name, mime_type, size_bytes, created_at, uploader:uploaded_by(id, full_name, email, avatar_url)',
        )
        .single();
      if (dbErr) {
        // Roll back the storage object — we don't want orphaned bytes
        // in the bucket. Best-effort: if the storage delete also
        // fails the user still sees the original metadata error.
        await supabase.storage.from(TASK_ATTACHMENTS_BUCKET).remove([path]);
        throw dbErr;
      }
      return data as unknown as TaskAttachmentWithUploader;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: taskKeys.attachments(vars.taskId) });
    },
  });
}

export function useDeleteTaskAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      taskId: string;
      storagePath: string;
    }) => {
      // Delete the metadata row first; if the user lacks permission
      // we short-circuit before touching Storage. RLS surface the
      // permission error verbatim.
      const { error: dbErr } = await supabase
        .from('task_attachments')
        .delete()
        .eq('id', input.id);
      if (dbErr) throw dbErr;
      // Best-effort cleanup of the bytes. We swallow storage errors
      // here because the row is already gone and a stale object is
      // less bad than a phantom row in the list.
      await supabase.storage
        .from(TASK_ATTACHMENTS_BUCKET)
        .remove([input.storagePath]);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: taskKeys.attachments(vars.taskId) });
    },
  });
}

/**
 * Mint a short-lived signed URL so the browser can preview an image
 * or download the file. 5 minutes is plenty for a single click; the
 * bucket is private so we don't want long-lived links floating
 * around.
 */
export function useTaskAttachmentSignedUrl(
  taskId: string | undefined,
  path: string | undefined,
) {
  return useQuery({
    queryKey:
      taskId && path
        ? taskKeys.attachmentUrl(taskId, path)
        : ['tasks', 'attachments', 'url', 'noop'],
    enabled: !!taskId && !!path,
    staleTime: 4 * 60 * 1000,
    queryFn: async () => {
      if (!path) return null;
      const { data, error } = await supabase.storage
        .from(TASK_ATTACHMENTS_BUCKET)
        .createSignedUrl(path, 60 * 5);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}
